<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import draggable from 'vuedraggable';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useCrmStore } from '../store/useCrmStore';
import { extractApiErrorMessage } from '../utils/error';
import { CRM_COLUMN_COLOR_TOKENS } from '../utils/columnColors';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  columns: {
    type: Array,
    default: () => [],
  },
  accountId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['close', 'updated']);

const { t } = useI18n();
const crmStore = useCrmStore();

const dialogRef = ref(null);
const editableColumns = ref([]);
const isSubmitting = ref(false);
const localDraftCount = ref(0);

const colorOptions = computed(() => {
  return CRM_COLUMN_COLOR_TOKENS.map(token => ({
    value: token,
    label: t(`CRM.COLUMN_COLORS.${token}`),
  }));
});

const localKey = column => {
  if (column.id) {
    return `persisted-${column.id}`;
  }

  localDraftCount.value += 1;
  return `draft-${localDraftCount.value}`;
};

const hydrateEditableColumns = () => {
  editableColumns.value = [...props.columns]
    .sort((a, b) => a.position - b.position)
    .map(column => ({
      id: column.id || null,
      name: column.name || '',
      color: column.color || 'slate',
      position: column.position || 0,
      local_id: localKey(column),
    }));
};

const appendColumn = () => {
  editableColumns.value.push({
    id: null,
    name: '',
    color: 'slate',
    position: editableColumns.value.length + 1,
    local_id: localKey({}),
  });
};

const onClose = () => {
  dialogRef.value?.close();
};

const saveColumn = async column => {
  const normalizedName = column.name.trim();
  if (!normalizedName) {
    useAlert(t('CRM.COLUMN_MANAGER.ERRORS.NAME_REQUIRED'));
    return;
  }

  isSubmitting.value = true;
  try {
    const payload = {
      name: normalizedName,
      color: column.color,
      position:
        editableColumns.value.findIndex(
          item => item.local_id === column.local_id
        ) + 1,
    };

    if (column.id) {
      await crmStore.updateKanbanColumn({
        accountId: props.accountId,
        columnId: column.id,
        columnData: payload,
        pipeline: 'service_orders',
      });
      useAlert(t('CRM.COLUMN_MANAGER.SUCCESS.UPDATE'));
    } else {
      await crmStore.createKanbanColumn({
        accountId: props.accountId,
        columnData: payload,
        pipeline: 'service_orders',
      });
      useAlert(t('CRM.COLUMN_MANAGER.SUCCESS.CREATE'));
    }
    emit('updated');
  } catch (error) {
    useAlert(
      extractApiErrorMessage(error, t('CRM.COLUMN_MANAGER.ERRORS.SAVE'))
    );
  } finally {
    isSubmitting.value = false;
  }
};

const deleteColumn = async column => {
  if (!column.id) {
    editableColumns.value = editableColumns.value.filter(
      item => item.local_id !== column.local_id
    );
    return;
  }

  isSubmitting.value = true;
  try {
    await crmStore.deleteKanbanColumn({
      accountId: props.accountId,
      columnId: column.id,
      pipeline: 'service_orders',
    });
    useAlert(t('CRM.COLUMN_MANAGER.SUCCESS.DELETE'));
    emit('updated');
  } catch (error) {
    useAlert(
      extractApiErrorMessage(error, t('CRM.COLUMN_MANAGER.ERRORS.DELETE'))
    );
  } finally {
    isSubmitting.value = false;
  }
};

const reorderColumns = async () => {
  const persistedIds = editableColumns.value
    .filter(column => column.id)
    .map(column => column.id);
  if (persistedIds.length <= 1) {
    return;
  }

  isSubmitting.value = true;
  try {
    await crmStore.reorderKanbanColumns({
      accountId: props.accountId,
      columnIdsInOrder: persistedIds,
      pipeline: 'service_orders',
    });
    useAlert(t('CRM.COLUMN_MANAGER.SUCCESS.REORDER'));
    emit('updated');
  } catch (error) {
    useAlert(
      extractApiErrorMessage(error, t('CRM.COLUMN_MANAGER.ERRORS.REORDER'))
    );
  } finally {
    isSubmitting.value = false;
  }
};

watch(
  () => props.show,
  async isVisible => {
    if (!isVisible) {
      dialogRef.value?.close();
      return;
    }

    hydrateEditableColumns();
    await nextTick();
    dialogRef.value?.open();
  }
);

watch(
  () => props.columns,
  () => {
    if (props.show) {
      hydrateEditableColumns();
    }
  }
);
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t('CRM.COLUMN_MANAGER.TITLE')"
    :description="$t('CRM.COLUMN_MANAGER.SUBTITLE')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="2xl"
    overflow-y-auto
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <p class="text-sm text-n-slate-10">
          {{ $t('CRM.COLUMN_MANAGER.HELP') }}
        </p>
        <NextButton
          icon="i-lucide-plus"
          size="sm"
          class="!whitespace-nowrap"
          :label="$t('CRM.COLUMN_MANAGER.ADD_COLUMN')"
          :disabled="isSubmitting"
          @click="appendColumn"
        />
      </div>

      <draggable
        :list="editableColumns"
        item-key="local_id"
        handle=".column-drag-handle"
        :animation="180"
        class="max-h-[65vh] space-y-3 overflow-y-auto pr-1"
        @end="reorderColumns"
      >
        <template #item="{ element }">
          <div class="rounded-xl border border-n-weak bg-n-solid-2 p-3">
            <div class="flex items-start gap-3">
              <button
                type="button"
                class="column-drag-handle inline-flex shrink-0 cursor-grab text-n-slate-10 active:cursor-grabbing"
                :title="$t('CRM.COLUMN_MANAGER.DRAG')"
              >
                <span class="i-lucide-grip-vertical size-4" />
              </button>

              <div class="min-w-0 flex-1">
                <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Input
                    v-model="element.name"
                    :label="$t('CRM.COLUMN_MANAGER.NAME_LABEL')"
                    :placeholder="$t('CRM.COLUMN_MANAGER.NAME_PLACEHOLDER')"
                  />
                  <div class="min-w-0 flex flex-col gap-1">
                    <label class="text-sm font-medium text-n-slate-12">
                      {{ $t('CRM.COLUMN_MANAGER.COLOR_LABEL') }}
                    </label>
                    <ComboBox
                      :model-value="element.color"
                      :options="colorOptions"
                      :placeholder="$t('CRM.COLUMN_MANAGER.COLOR_PLACEHOLDER')"
                      :search-placeholder="
                        $t('CRM.COLUMN_MANAGER.COLOR_SEARCH')
                      "
                      :empty-state="$t('CRM.COLUMN_MANAGER.COLOR_EMPTY')"
                      @update:model-value="value => (element.color = value)"
                    />
                  </div>
                </div>

                <div
                  class="mt-3 flex flex-wrap items-center justify-end gap-2 border-t border-n-weak pt-3"
                >
                  <NextButton
                    size="sm"
                    color="slate"
                    variant="outline"
                    class="!whitespace-nowrap"
                    :label="$t('CRM.COLUMN_MANAGER.SAVE_COLUMN')"
                    :is-loading="isSubmitting"
                    @click="saveColumn(element)"
                  />
                  <NextButton
                    size="sm"
                    color="ruby"
                    variant="ghost"
                    icon="i-lucide-trash-2"
                    :title="$t('CRM.COLUMN_MANAGER.DELETE_COLUMN')"
                    :disabled="isSubmitting"
                    @click="deleteColumn(element)"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
      </draggable>

      <div class="flex items-center justify-end">
        <NextButton
          size="sm"
          color="slate"
          variant="ghost"
          :label="$t('CRM.COLUMN_MANAGER.CLOSE')"
          @click="onClose"
        />
      </div>
    </div>
  </Dialog>
</template>
